import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminAccess, adminAddStaff, adminPatchStaff, adminSsoRules, adminPutSsoRules, getMe,
  adminRegistryLists, adminPutRegistryLists,
  adminComplianceItemTypes, adminComplianceCreateItemType, adminComplianceUpdateItemType, adminComplianceReseed,
  apiErrText, COMPLIANCE_CADENCE, COMPLIANCE_CATEGORY, COMPLIANCE_CODE_RE, type ComplianceItemTypeRow, type ComplianceCadence,
  listDocTemplates, docTemplatePlaceholders, uploadDocTemplate, activateDocTemplate, archiveDocTemplate, deleteDocTemplate, validateDocTemplate,
  getInstructionSettings, saveInstructionSettings, VARIATION_STANDARD_LABEL, type InstructionSettings,
  ROLE_LABEL, type AccessStaff, type RoleRule, type ApiError, type MeResponse, type DocTemplate,
} from '../lib/api';
import { Skeleton } from '../components/ui/Skeleton';
import { toast } from '../components/ui/Toast';

/**
 * 管理 / Admin (spec 2026-07-08) — the GM's control surface over WHO can use this app and AS WHAT:
 * 成员 (role + 启用/停用, 停用 kills their sessions immediately) · 待审批 (WeCom sign-ins no
 * department rule admitted — one click to let them in) · 部门规则 (the WeCom allowlist, live).
 */
export default function Admin() {
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: getMe });
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin-access'], queryFn: adminAccess, enabled: me?.account?.role === 'gm' });
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin-access'] });

  // 估价 (budget) manages the contract/annexure doc templates but NOT access — show only that panel.
  if (me && me.account?.role === 'budget') {
    return (
      <div>
        <h2 style={{ margin: '4px 0' }}>文档模板 / Doc templates</h2>
        <DocTemplatesPanel />
      </div>
    );
  }
  if (me && me.account?.role !== 'gm') return <div className="card">此页仅总经理可见 / GM only.</div>;
  if (isLoading || !data) return <div className="card"><Skeleton lines={5} /></div>;

  return (
    <div>
      <h2 style={{ margin: '4px 0' }}>访问管理 / Access Admin</h2>
      <p className="muted small" style={{ marginTop: 0 }}>
        谁能进入本系统、以什么角色 — 企业微信扫码按下方「部门规则」自动放行；不在规则内的人会出现在「待审批」。
        停用一个人会<strong>立即</strong>踢掉其登录。 / Who gets in and as what; deactivation kicks them out immediately.
      </p>

      {data.pending.length > 0 && (
        <div className="card" style={{ borderColor: 'var(--warn)', marginBottom: 14 }}>
          <strong>待审批 / Pending ({data.pending.length})</strong>
          <div className="muted small">这些同事扫码请求过访问，但不在任何部门规则内。选角色开通，或不理会。</div>
          {data.pending.map((p) => <PendingRow key={p.id} p={p} roles={data.assignable_roles} onDone={refresh} />)}
        </div>
      )}

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="row" style={{ alignItems: 'center' }}>
          <strong>成员 / Members ({data.members.length})</strong>
          <span style={{ flex: 1 }} />
          <AddMemberForm roles={data.assignable_roles} onDone={refresh} />
        </div>
        <div className="cmp-wrap"><table className="cmp"><thead><tr>
          <th>姓名 / Name</th><th>部门·职位</th><th>账号</th><th>角色 / Role</th><th>最近登录 / Last sign-in</th><th></th>
        </tr></thead><tbody>
          {data.members.map((m) => <MemberRow key={m.id} m={m} roles={data.assignable_roles} self={me?.account?.subject_ref === m.id} onDone={refresh} />)}
        </tbody></table></div>
      </div>

      <RulesEditor />
      <RegistryListsEditor />
      <ComplianceItemTypesEditor />
      <InstructionSettingsPanel />
      <DocTemplatesPanel />
    </div>
  );
}

/** Chunked base64 — `String.fromCharCode(...bytes)` throws RangeError past ~64K args, so any
 *  letterheaded/imaged .docx would crash the old one-shot spread. 32KB slices keep every file safe. */
async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const CHUNK = 32768;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/** 合同/验收标准确认书 .docx templates — upload, validation report (unknown/missing tags), activate. */
function DocTemplatesPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['doc-templates'], queryFn: listDocTemplates });
  const { data: catalog } = useQuery({ queryKey: ['doc-template-placeholders'], queryFn: docTemplatePlaceholders });
  const [kind, setKind] = useState<'contract' | 'annexure'>('contract');
  const [busy, setBusy] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const r = await uploadDocTemplate(kind, file.name, await fileToBase64(file));
      const v = r.template.validation;
      if (r.duplicate) {
        toast('该文件与已上传的模板内容完全相同 — 已复用现有记录，未重复添加。 / identical to an existing template; reused.', { kind: 'info' });
      } else if (v.no_tags) {
        // the "wrong file" signature: compiled cleanly but contains zero {tags} — almost certainly
        // the un-tagged original contract instead of the placeholder-tagged copy
        toast('该文件没有任何 {占位符} — 可能上传了未标记的原始合同。请上传"已标记占位符"的版本。 / This file has no placeholders — likely the untagged original.', { kind: 'error' });
      } else if (v.errors.length || v.missing.length || v.unknown.length) {
        toast(`已上传，但存在问题：结构错误 ${v.errors.length} · 缺失必填 ${v.missing.length} · 未知占位符 ${v.unknown.length}`, { kind: 'error' });
      } else {
        toast('模板已上传，校验通过 — 可启用。 / uploaded, validation clean', { kind: 'success' });
      }
      qc.invalidateQueries({ queryKey: ['doc-templates'] });
      qc.invalidateQueries({ queryKey: ['contract-templates'] });
    } catch (err) { toast((err as ApiError)?.body?.detail ?? (err as ApiError)?.body?.error ?? '上传失败 / upload failed', { kind: 'error' }); }
    finally { setBusy(false); }
  }
  async function revalidate(t: DocTemplate) {
    setBusy(true);
    try { await validateDocTemplate(t.id); toast('已重新校验 / revalidated', { kind: 'success' }); qc.invalidateQueries({ queryKey: ['doc-templates'] }); qc.invalidateQueries({ queryKey: ['contract-templates'] }); }
    catch (e) { toast((e as ApiError)?.body?.error ?? '校验失败 / revalidation failed', { kind: 'error' }); }
    finally { setBusy(false); }
  }
  async function activate(t: DocTemplate) {
    setBusy(true);
    try { await activateDocTemplate(t.id); toast('已启用 / activated', { kind: 'success' }); qc.invalidateQueries({ queryKey: ['doc-templates'] }); }
    catch (e) { toast((e as ApiError)?.body?.detail ?? '启用失败 — 模板校验未通过 / activation refused', { kind: 'error' }); }
    finally { setBusy(false); }
  }
  // Eric 9/18: 「该模板已用于 N 次合同生成，不能删除」 → archive instead (hidden from the picker, audit rows kept).
  async function archive(t: DocTemplate, archived: boolean) {
    setBusy(true);
    try {
      await archiveDocTemplate(t.id, archived);
      toast(archived ? '已归档 — 不再出现在生成合同的模板选择中 / archived' : '已恢复 / restored', { kind: 'success' });
      qc.invalidateQueries({ queryKey: ['doc-templates'] }); qc.invalidateQueries({ queryKey: ['contract-templates'] });
    } catch (e) { toast((e as ApiError)?.body?.detail ?? '操作失败 / failed', { kind: 'error' }); }
    finally { setBusy(false); }
  }
  async function remove(t: DocTemplate) {
    if (!window.confirm(`删除模板「${t.file_name}」？此操作不可撤销。`)) return;
    setBusy(true);
    try { await deleteDocTemplate(t.id); toast('已删除 / deleted', { kind: 'success' }); qc.invalidateQueries({ queryKey: ['doc-templates'] }); qc.invalidateQueries({ queryKey: ['contract-templates'] }); }
    catch (e) { toast((e as ApiError)?.body?.detail ?? '删除失败 / delete failed', { kind: 'error' }); }
    finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <strong>文档模板 / Doc templates（合同 + 验收标准确认书 Word）</strong>
      <div className="muted small" style={{ marginBottom: 8 }}>
        上传公司的合同/验收标准确认书 Word 模板（用 {'{占位符}'} 标记数据位置 — 见下方目录；在 Word 里请「粘贴」占位符并关闭自动更正，花括号被替换成全角会导致校验失败）。
        校验通过才能启用。多个合同模板可以同时存在（例如固定单价、固定总价）——生成合同时可逐次选择使用哪一个，「启用」只决定默认选中哪一个。验收标准确认书未上传模板时使用系统内置版式。
        {data?.default_annexure_builtin && <span className="pill pill-muted small" style={{ marginLeft: 6 }}>验收标准确认书：内置版式 / built-in</span>}
      </div>
      <div className="row-wrap" style={{ alignItems: 'center', marginBottom: 8 }}>
        <select value={kind} onChange={(e) => setKind(e.target.value as 'contract' | 'annexure')}>
          <option value="contract">合同模板 / contract</option>
          <option value="annexure">验收标准确认书模板 / annexure</option>
        </select>
        <label className="small" style={{ cursor: 'pointer' }}>
          <input type="file" accept=".docx" style={{ display: 'none' }} disabled={busy} onChange={onPick} />
          <span className="pill pill-stage">⬆ 上传 .docx / Upload</span>
        </label>
        <span className="spacer" />
        <button className="small" onClick={() => setShowCatalog(!showCatalog)}>{showCatalog ? '收起占位符目录' : '📖 占位符目录 / Placeholders'}</button>
      </div>

      {showCatalog && catalog && (
        <div className="cmp-wrap" style={{ marginBottom: 8 }}>
          <table className="cmp small"><thead><tr><th>占位符</th><th>说明</th><th>示例</th><th>必填</th><th>类型</th></tr></thead>
            <tbody>
              {(kind === 'contract' ? catalog.contract : catalog.annexure).map((p) => (
                <tr key={p.name}>
                  <td><code>{p.kind === 'loop' || p.kind === 'section' ? `{#${p.name}}…{/${p.name}}` : `{${p.name}}`}</code></td>
                  <td>{p.zh}</td><td className="muted">{p.sample}</td>
                  <td>{p.required ? '✓' : ''}</td><td className="muted">{p.kind ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(data?.templates ?? []).map((t) => {
        const v = t.validation;
        const clean = !v.errors.length && !v.missing.length;
        return (
          <div key={t.id} style={{ borderTop: '1px solid var(--rule, #efe9dc)', padding: '6px 0' }}>
            <div className="row-wrap small" style={{ alignItems: 'center' }}>
              <span className="pill pill-muted small">{t.kind === 'contract' ? '合同' : '验收标准确认书'}</span>
              <strong>{t.file_name}</strong>
              {t.active && <span className="pill pill-good small">默认 ✓ / default</span>}
              {t.archived && <span className="pill pill-muted small" title={`归档于 ${t.archived_at?.slice(0, 10) ?? ''} — 生成合同时不再可选，历史生成记录保留`}>已归档 / archived</span>}
              <span className="muted">{t.created_at.slice(0, 16).replace('T', ' ')} · {t.uploaded_by ?? ''}</span>
              <span className="spacer" />
              {!t.active && !t.archived && <button className="small btn-primary" disabled={busy || !clean} title={clean ? '设为生成合同时的默认选项' : '校验未通过，不能设为默认'} onClick={() => activate(t)}>设为默认 / Set default</button>}
              {!t.archived && <button className="small" disabled={busy} title="按最新占位符目录重新校验" onClick={() => revalidate(t)}>↻ 重新校验</button>}
              {t.archived
                ? <button className="small" disabled={busy} title="恢复后重新出现在模板选择中" onClick={() => archive(t, false)}>↩ 恢复 / Restore</button>
                : <button className="small" disabled={busy} title="从生成合同的模板选择中隐藏；已用于合同生成的模板只能归档，不能删除" onClick={() => archive(t, true)}>📦 归档 / Archive</button>}
              <button className="small" disabled={busy} title="删除该模板（已用于合同生成的模板不能删，请用归档）" onClick={() => remove(t)}>🗑 删除</button>
            </div>
            {v.no_tags ? (
              <div className="small" style={{ marginTop: 4, color: 'var(--bad)' }}>
                ✗ 该文件没有任何 {'{占位符}'} — 可能上传了未标记的原始合同。请上传"已标记占位符"的版本（文件名含「已标记占位符」）。
              </div>
            ) : (v.errors.length > 0 || v.missing.length > 0 || v.unknown.length > 0) && (
              <div className="small" style={{ marginTop: 4 }}>
                {v.errors.map((e2, i) => <div key={i} style={{ color: 'var(--bad)' }}>✗ 结构错误：{e2.tag ? `{${e2.tag}} — ` : ''}{e2.explanation}</div>)}
                {v.missing.length > 0 && <div style={{ color: 'var(--bad)' }}>✗ 缺失必填占位符：{v.missing.map((m) => `{${m}}`).join(' ')}</div>}
                {v.unknown.length > 0 && <div style={{ color: 'var(--warn, #b8860b)' }}>⚠ 未知占位符（拼写/全角括号？）：{v.unknown.map((u) => `{${u}}`).join(' ')}</div>}
              </div>
            )}
          </div>
        );
      })}
      {(data?.templates ?? []).length === 0 && <div className="muted small">尚未上传模板。/ No templates yet.</div>}
    </div>
  );
}

function errText(e: unknown, fallback: string) { return (e as ApiError)?.body?.error ?? fallback; }

/** 添加成员 — pre-add a person (e.g. 高强 / SCRUM / exec) before their first WeCom scan; the scan is
 *  then linked by name automatically and keeps the role chosen here. */
function AddMemberForm({ roles, onDone }: { roles: string[]; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [dept, setDept] = useState('');
  const [role, setRole] = useState('');
  const [busy, setBusy] = useState(false);
  async function add() {
    setBusy(true);
    try {
      await adminAddStaff({ name: name.trim(), dept: dept.trim() || undefined, role });
      toast(`已添加 ${name.trim()} — 其首次企微扫码将自动关联并按此角色进入 / added; first WeCom scan links automatically`, { kind: 'success' });
      setName(''); setDept(''); setRole(''); setOpen(false); onDone();
    } catch (e) { toast(errText(e, '添加失败 / failed'), { kind: 'error' }); } finally { setBusy(false); }
  }
  if (!open) return <button className="small btn-primary" onClick={() => setOpen(true)}>＋ 添加成员 / Add member</button>;
  return (
    <span className="row" style={{ gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="姓名（与企微一致）" style={{ width: 130 }} autoFocus />
      <input value={dept} onChange={(e) => setDept(e.target.value)} placeholder="部门 如: SCRUM" style={{ width: 120 }} />
      <select value={role} onChange={(e) => setRole(e.target.value)}>
        <option value="">选择角色 / role…</option>
        {roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] ?? r}</option>)}
      </select>
      <button className="btn-primary small" disabled={busy || !name.trim() || !role} onClick={add}>{busy ? '…' : '添加 / Add'}</button>
      <button className="small" disabled={busy} onClick={() => setOpen(false)}>取消</button>
    </span>
  );
}

function PendingRow({ p, roles, onDone }: { p: AccessStaff; roles: string[]; onDone: () => void }) {
  const [role, setRole] = useState('');
  const [busy, setBusy] = useState(false);
  async function approve() {
    setBusy(true);
    try { await adminPatchStaff(p.id, { role, active: true }); toast(`已开通 ${p.name} / approved`, { kind: 'success' }); onDone(); }
    catch (e) { toast(errText(e, '开通失败 / failed'), { kind: 'error' }); } finally { setBusy(false); }
  }
  return (
    <div className="row" style={{ gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
      <strong>{p.name}</strong>
      <span className="muted small">{[p.dept, p.position].filter(Boolean).join(' · ') || '—'}</span>
      <span style={{ flex: 1 }} />
      <select value={role} onChange={(e) => setRole(e.target.value)}>
        <option value="">选择角色 / pick a role…</option>
        {roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] ?? r}</option>)}
      </select>
      <button className="btn-primary small" disabled={!role || busy} onClick={approve}>{busy ? '…' : '开通 / Approve'}</button>
    </div>
  );
}

function MemberRow({ m, roles, self, onDone }: { m: AccessStaff; roles: string[]; self: boolean; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  async function patch(body: { role?: string; active?: boolean }, okMsg: string) {
    setBusy(true);
    try { await adminPatchStaff(m.id, body); toast(okMsg, { kind: 'success' }); onDone(); }
    catch (e) { toast(errText(e, '操作失败 / failed'), { kind: 'error' }); } finally { setBusy(false); }
  }
  return (
    <tr style={!m.active ? { opacity: 0.55 } : undefined}>
      <td><strong>{m.name}</strong>{self && <span className="pill small pill-stage" style={{ marginLeft: 6 }}>你 / you</span>}</td>
      <td className="muted small">{[m.dept, m.position].filter(Boolean).join(' · ') || '—'}</td>
      <td className="muted small">{m.login_id ?? (m.wecom_userid ? '企微 / WeCom' : '—')}</td>
      <td>
        <select value={m.role} disabled={busy || self} onChange={(e) => patch({ role: e.target.value }, '角色已更新 / role updated')}>
          {roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] ?? r}</option>)}
        </select>
      </td>
      <td className="muted small">{m.last_login_at ? m.last_login_at.slice(0, 16).replace('T', ' ') : '从未 / never'}</td>
      <td>
        {m.active
          ? <button className="small" disabled={busy || self} title={self ? '不能停用自己' : '立即踢出并禁止登录'}
              onClick={() => { if (window.confirm(`停用「${m.name}」？其登录会话将立即失效。 / Deactivate?`)) patch({ active: false }, '已停用 / deactivated'); }}>停用 / Disable</button>
          : <button className="small btn-primary" disabled={busy} onClick={() => patch({ active: true }, '已启用 / re-enabled')}>启用 / Enable</button>}
      </td>
    </tr>
  );
}

/** The WeCom allowlist — ordered keyword rules; first match wins. Saved as a whole list. */
function RulesEditor() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin-sso-rules'], queryFn: adminSsoRules });
  const [draft, setDraft] = useState<RoleRule[] | null>(null);
  const [busy, setBusy] = useState(false);
  if (!data) return null;
  const rules = draft ?? data.rules;

  const set = (i: number, patch: Partial<RoleRule>) => setDraft(rules.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  async function save() {
    setBusy(true);
    try {
      await adminPutSsoRules(rules);
      toast('规则已保存，下次扫码即生效 / rules saved', { kind: 'success' });
      setDraft(null);
      qc.invalidateQueries({ queryKey: ['admin-sso-rules'] });
    } catch (e) { toast(errText(e, '保存失败 / failed'), { kind: 'error' }); } finally { setBusy(false); }
  }
  return (
    <div className="card">
      <strong>部门规则 / WeCom department rules</strong>
      <div className="muted small" style={{ marginBottom: 8 }}>
        扫码人的「直属部门 + 职位」包含关键词即按该角色放行（从上到下，先中先得；含排除词则跳过）。删掉规则 = 该部门改走待审批。
      </div>
      {rules.map((r, i) => (
        <div key={i} className="row" style={{ gap: 6, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
          <input value={r.keywords.join(' ')} title="关键词（空格分隔）" style={{ width: 200 }}
            onChange={(e) => set(i, { keywords: e.target.value.split(/\s+/).filter(Boolean) })} placeholder="关键词 如: 预算 造价" />
          <input value={(r.not_keywords ?? []).join(' ')} title="排除词（可空）" style={{ width: 110 }}
            onChange={(e) => set(i, { not_keywords: e.target.value.split(/\s+/).filter(Boolean) })} placeholder="排除 如: 副" />
          <span className="muted small">→</span>
          <select value={r.role} onChange={(e) => set(i, { role: e.target.value })}>
            {data.assignable_roles.map((role) => <option key={role} value={role}>{ROLE_LABEL[role] ?? role}</option>)}
          </select>
          <button className="small" title="删除此规则" onClick={() => setDraft(rules.filter((_, j) => j !== i))}>✕</button>
        </div>
      ))}
      <div className="row" style={{ gap: 8, marginTop: 8 }}>
        <button className="small" onClick={() => setDraft([...rules, { keywords: [], role: data.assignable_roles[0] }])}>＋ 加一条 / add rule</button>
        <span style={{ flex: 1 }} />
        {draft && <button className="small" disabled={busy} onClick={() => setDraft(null)}>放弃修改 / discard</button>}
        {draft && <button className="btn-primary small" disabled={busy || rules.some((r) => !r.keywords.length)} onClick={save}>{busy ? '…' : '保存规则 / Save rules'}</button>}
      </div>
    </div>
  );
}

/** 供应商分类 & 城市 (改改 fb_c09bf7fd / fb_73269add) — the two pickable lists behind 供应商名录's
 *  类型/城市 columns. Saved as whole lists; list order is display order. */
function RegistryListsEditor() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin-registry-lists'], queryFn: adminRegistryLists });
  const [draft, setDraft] = useState<{ sub_types: string[]; cities: string[] } | null>(null);
  const [busy, setBusy] = useState(false);
  if (!data) return null;
  const lists = draft ?? { sub_types: data.sub_types, cities: data.cities };

  const set = (key: 'sub_types' | 'cities', items: string[]) => setDraft({ ...lists, [key]: items });
  async function save() {
    setBusy(true);
    try {
      await adminPutRegistryLists({
        sub_types: lists.sub_types.map((s) => s.trim()).filter(Boolean),
        cities: lists.cities.map((s) => s.trim()).filter(Boolean),
      });
      toast('已保存 / saved', { kind: 'success' });
      setDraft(null);
      qc.invalidateQueries({ queryKey: ['admin-registry-lists'] });
      qc.invalidateQueries({ queryKey: ['me'] });
    } catch (e) { toast(errText(e, '保存失败 / failed'), { kind: 'error' }); } finally { setBusy(false); }
  }

  const column = (key: 'sub_types' | 'cities', title: string) => (
    <div style={{ minWidth: 200 }}>
      <div className="muted small" style={{ marginBottom: 4 }}>{title}</div>
      {lists[key].map((v, i) => (
        <div key={i} className="row" style={{ gap: 4, alignItems: 'center', marginBottom: 4 }}>
          <input value={v} style={{ width: 130 }}
            onChange={(e) => set(key, lists[key].map((x, j) => (j === i ? e.target.value : x)))} />
          <button className="small" title="删除此项" onClick={() => set(key, lists[key].filter((_, j) => j !== i))}>✕</button>
        </div>
      ))}
      <button className="small" onClick={() => set(key, [...lists[key], ''])}>＋ 加一项 / add</button>
    </div>
  );

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <strong>供应商分类 &amp; 城市 / Supplier types &amp; cities</strong>
      <div className="muted small" style={{ marginBottom: 8 }}>
        类型为多选（劳务 → 装修/拆除…），城市也可在名录页临时新增（采购/估价/GM）。删除某项不会清空已有供应商上的值。
      </div>
      <div className="row-wrap" style={{ gap: 24 }}>
        {column('sub_types', `二级类型 / sub-types（${data.category}）`)}
        {column('cities', '城市 / cities')}
      </div>
      <div className="row" style={{ gap: 8, marginTop: 8 }}>
        <span style={{ flex: 1 }} />
        {draft && <button className="small" disabled={busy} onClick={() => setDraft(null)}>放弃修改 / discard</button>}
        {draft && <button className="btn-primary small" disabled={busy} onClick={save}>{busy ? '…' : '保存 / Save'}</button>}
      </div>
    </div>
  );
}

/**
 * 履约档案项目 / Compliance item types (Eric 9/18, Layer 1 step 4B) — the GM's catalog.
 *
 * WHY a table and not settings JSON: getSettings' deepMerge replaces arrays wholesale, so once a GM
 * edited one item in settings a later code-side seed addition could never reach prod again (decision
 * D1). The catalog is therefore `compliance_item_types` rows, seeded with INSERT OR IGNORE — the same
 * shape as bid_rules.
 *
 * Two rules the UI has to carry, both server-enforced:
 *   · There is NO delete. 停用 (active = 0) retires an item; its existing slots stay readable, which is
 *     the point — the 履约档案 is evidence.
 *   · 频次 is LOCKED once any slot snapshotted it (409 `cadence_locked`). The slot-uniqueness index is
 *     partial on cadence <> 'per_event', so flipping the catalog would silently disarm it. The select
 *     is disabled for the rest of the session as soon as the server says so, with its own wording.
 */
function ComplianceItemTypesEditor() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin-compliance-item-types'], queryFn: adminComplianceItemTypes });
  const [drafts, setDrafts] = useState<Record<string, Partial<ComplianceItemTypeRow>>>({});
  // codes whose cadence the server has already refused to change — disable the select from then on
  const [cadenceLocked, setCadenceLocked] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  if (!data) return null;

  const refresh = () => qc.invalidateQueries({ queryKey: ['admin-compliance-item-types'] });
  const edit = (code: string, patch: Partial<ComplianceItemTypeRow>) =>
    setDrafts((d) => ({ ...d, [code]: { ...d[code], ...patch } }));
  const val = <K extends keyof ComplianceItemTypeRow>(t: ComplianceItemTypeRow, k: K): ComplianceItemTypeRow[K] =>
    (drafts[t.code]?.[k] ?? t[k]) as ComplianceItemTypeRow[K];

  async function save(t: ComplianceItemTypeRow) {
    const patch = drafts[t.code];
    if (!patch || Object.keys(patch).length === 0) return;
    setBusy(t.code);
    try {
      await adminComplianceUpdateItemType(t.code, patch);
      toast(`「${t.code}」已保存 / saved`, { kind: 'success' });
      setDrafts((d) => { const n = { ...d }; delete n[t.code]; return n; });
      refresh();
    } catch (e) {
      const code = (e as ApiError)?.body?.error;
      const detail = apiErrText(e);
      // the 409 is the ONLY signal the server gives that slots already exist for this code
      if (code === 'cadence_locked') {
        setCadenceLocked((m) => ({ ...m, [t.code]: detail }));
        setDrafts((d) => { const n = { ...d }; if (n[t.code]) delete n[t.code].cadence; return n; });
      }
      toast(detail, { kind: 'error' });
    } finally { setBusy(null); }
  }

  /** 启用/停用 is a one-click toggle — it is the only way to retire an item, so it does not wait for 保存. */
  async function toggleActive(t: ComplianceItemTypeRow) {
    setBusy(t.code);
    try {
      await adminComplianceUpdateItemType(t.code, { active: t.active ? 0 : 1 });
      toast(t.active ? `「${t.code}」已停用 / switched off` : `「${t.code}」已启用 / switched on`, { kind: 'success' });
      refresh();
    } catch (e) { toast(apiErrText(e), { kind: 'error' }); } finally { setBusy(null); }
  }

  async function reseed() {
    if (!confirm('恢复默认项：把代码里的默认档案项目补回目录（已有项目的名称、频次等改动不受影响，不会删除任何项）。继续？\n/ Re-add any missing default item — your own wording on existing rows is untouched and nothing is deleted. Continue?')) return;
    setBusy('reseed');
    try {
      const r = await adminComplianceReseed();
      toast(`已补回 ${r.inserted} 项，目录共 ${r.total} 项 / re-added ${r.inserted}, ${r.total} total`, { kind: 'success' });
      refresh();
    } catch (e) { toast(apiErrText(e), { kind: 'error' }); } finally { setBusy(null); }
  }

  const rows = data.item_types;
  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="row-wrap" style={{ alignItems: 'center', gap: 8 }}>
        <strong>履约档案项目 / Compliance item types（{rows.length}）</strong>
        <span style={{ flex: 1 }} />
        <button className="small" onClick={() => setShowAdd((v) => !v)}>＋ 新增项目 / Add an item</button>
        <button className="small" disabled={busy === 'reseed'} onClick={reseed}>恢复默认项 / Reseed defaults</button>
      </div>
      <div className="muted small" style={{ marginBottom: 8 }}>
        合同条款项来自代码默认；公司自定要求请新增并勾「政策项」。<strong>没有删除</strong> —— 不再需要的项请「停用」，
        已产生的记录仍可查。频次一旦被合同档案使用即锁定。
        / Contract-clause rows come from the seed; company-only requirements are added here and flagged 政策项.
        There is <strong>no delete</strong> — switch an item off instead; 频次 locks once any slot uses it.
      </div>

      {showAdd && <AddComplianceItemType data={data} onDone={() => { setShowAdd(false); refresh(); }} />}

      <div className="cmp-wrap" style={{ overflowX: 'auto' }}>
        <table className="cmp"><thead><tr>
          <th>编号</th>
          <th style={{ minWidth: 160 }}>名称（中）/ label zh</th>
          <th style={{ minWidth: 160 }}>名称（英）/ label en</th>
          <th>必填</th><th>政策项</th>
          <th>频次 / Cadence</th>
          <th>适用形式 / Form</th>
          <th>有效期</th><th>启用</th><th></th>
        </tr></thead><tbody>
          {rows.map((t) => {
            const dirty = !!drafts[t.code] && Object.keys(drafts[t.code]).length > 0;
            const locked = cadenceLocked[t.code];
            return (
              <tr key={t.id} style={t.active ? undefined : { opacity: 0.55 }}>
                <td><strong>{t.code}</strong><div className="muted small">{COMPLIANCE_CATEGORY[t.category]?.split(' / ')[0] ?? t.category}</div></td>
                <td><input value={String(val(t, 'label_zh') ?? '')} onChange={(e) => edit(t.code, { label_zh: e.target.value })} /></td>
                <td><input value={String(val(t, 'label_en') ?? '')} onChange={(e) => edit(t.code, { label_en: e.target.value })} /></td>
                <td><input type="checkbox" checked={!!val(t, 'required')} onChange={(e) => edit(t.code, { required: e.target.checked ? 1 : 0 })} /></td>
                <td><input type="checkbox" checked={!!val(t, 'policy_only')} title="公司要求，非合同条款 / WTL policy — not a contract clause"
                  onChange={(e) => edit(t.code, { policy_only: e.target.checked ? 1 : 0 })} /></td>
                <td>
                  <select value={String(val(t, 'cadence'))} disabled={!!locked}
                    title={locked ?? '频次决定清单何时生成该项 / when the slot is created'}
                    onChange={(e) => edit(t.code, { cadence: e.target.value as ComplianceItemTypeRow['cadence'] })}>
                    {data.cadences.map((c) => <option key={c} value={c}>{COMPLIANCE_CADENCE[c as ComplianceCadence] ?? c}</option>)}
                  </select>
                  {locked && <div className="small" style={{ color: 'var(--bad)', whiteSpace: 'normal', maxWidth: 260 }}>{locked}</div>}
                </td>
                <td>
                  <select value={String(val(t, 'form_applicability'))}
                    onChange={(e) => edit(t.code, { form_applicability: e.target.value as ComplianceItemTypeRow['form_applicability'] })}>
                    {data.form_applicability.map((f) => (
                      <option key={f} value={f}>{f === 'both' ? '两种 / both' : f === 'unit_price' ? '固定单价' : '固定总价'}</option>
                    ))}
                  </select>
                </td>
                <td><input type="checkbox" checked={!!val(t, 'expiry_tracked')} title="上传时必须填有效期 / the upload must carry a validity date"
                  onChange={(e) => edit(t.code, { expiry_tracked: e.target.checked ? 1 : 0 })} /></td>
                <td>
                  <button className="small" disabled={busy === t.code} onClick={() => toggleActive(t)}>
                    {t.active ? '停用' : '启用'}
                  </button>
                </td>
                <td>
                  {dirty && (
                    <span className="row" style={{ gap: 4 }}>
                      <button className="btn-primary small" disabled={busy === t.code} onClick={() => save(t)}>保存</button>
                      <button className="small" onClick={() => setDrafts((d) => { const n = { ...d }; delete n[t.code]; return n; })}>放弃</button>
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody></table>
      </div>
    </div>
  );
}

/** ＋ 新增项目 — company-specific requirements. The code is the seed key, so it can never be edited later. */
function AddComplianceItemType(
  { data, onDone }: { data: { cadences: string[]; owner_sides: string[]; form_applicability: string[]; scopes: string[] }; onDone: () => void },
) {
  const [code, setCode] = useState('');
  const [zh, setZh] = useState('');
  const [en, setEn] = useState('');
  const [cadence, setCadence] = useState('once');
  const [ownerSide, setOwnerSide] = useState('labor');
  const [required, setRequired] = useState(true);
  const [policyOnly, setPolicyOnly] = useState(true);
  const [expiry, setExpiry] = useState(false);
  const [busy, setBusy] = useState(false);
  const codeOk = COMPLIANCE_CODE_RE.test(code.trim().toUpperCase());

  async function add() {
    setBusy(true);
    try {
      await adminComplianceCreateItemType({
        code: code.trim().toUpperCase(), label_zh: zh.trim(), label_en: en.trim(), cadence,
        owner_side: ownerSide, required: required ? 1 : 0, policy_only: policyOnly ? 1 : 0,
        expiry_tracked: expiry ? 1 : 0,
      });
      toast('已新增 / added', { kind: 'success' });
      setCode(''); setZh(''); setEn('');
      onDone();
    } catch (e) { toast(apiErrText(e), { kind: 'error' }); } finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ background: 'var(--stripe)', marginBottom: 10 }}>
      <div className="row-wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
        <div className="field" style={{ marginBottom: 0, width: 90 }}>
          <label>编号 / code</label>
          <input value={code} placeholder="H9" onChange={(e) => setCode(e.target.value)} />
        </div>
        <div className="field grow" style={{ marginBottom: 0 }}>
          <label>名称（中）/ label zh</label>
          <input value={zh} onChange={(e) => setZh(e.target.value)} placeholder="例如：班组长月度考勤汇总" />
        </div>
        <div className="field grow" style={{ marginBottom: 0 }}>
          <label>名称（英）/ label en</label>
          <input value={en} onChange={(e) => setEn(e.target.value)} placeholder="Monthly foreman attendance summary" />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>频次 / cadence</label>
          <select value={cadence} onChange={(e) => setCadence(e.target.value)}>
            {data.cadences.map((c) => <option key={c} value={c}>{COMPLIANCE_CADENCE[c as ComplianceCadence] ?? c}</option>)}
          </select>
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>责任方 / owner</label>
          <select value={ownerSide} onChange={(e) => setOwnerSide(e.target.value)}>
            {data.owner_sides.map((o) => (
              <option key={o} value={o}>{o === 'labor' ? '乙方 / labor' : o === 'wtl' ? '我方 / WTL' : '双方 / both'}</option>
            ))}
          </select>
        </div>
        <label className="row small" style={{ gap: 4, alignItems: 'center' }}>
          <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} /> 必填
        </label>
        <label className="row small" style={{ gap: 4, alignItems: 'center' }}
          title="公司要求，非合同条款 / WTL policy — not a contract clause">
          <input type="checkbox" checked={policyOnly} onChange={(e) => setPolicyOnly(e.target.checked)} /> 政策项
        </label>
        <label className="row small" style={{ gap: 4, alignItems: 'center' }}>
          <input type="checkbox" checked={expiry} onChange={(e) => setExpiry(e.target.checked)} /> 有效期
        </label>
        <button className="btn-primary small" disabled={busy || !codeOk || !zh.trim() || !en.trim()} onClick={add}>新增 / Add</button>
      </div>
      {code && !codeOk && (
        <div className="small" style={{ color: 'var(--bad)', marginTop: 4 }}>
          编号须为一个字母 A–H 加 1–2 位数字，如 H9 / the code must be a letter A–H plus one or two digits, e.g. H9
        </div>
      )}
    </div>
  );
}

/**
 * 工程指令设置 / Instruction settings — gm only (Eric 9/18 (工程指令)).
 *
 * Three of these were open company questions when Layer 2 shipped, and each one shipped INERT rather
 * than guessed:
 *   · 容错额度 (谢工 7/24 — "公司拍板"): `tolerance_rmb: null` means no routing gate at all. Set a figure
 *     and pick 提示 vs 拦截; 提示 only warns, which is what the company asked for until it decides.
 *   · 节假日: there is no CN public-holiday calendar in the box, so 工作日 currently means "not a weekend".
 *     Every 工作日 clock (返工确认 3 天, 计日工核实 2 天) is wrong across 春节 until this list is pasted in.
 *   · 零星限价 (`daywork_rate_caps`): 谢工's 统一限价表 has not arrived, so it stays empty and 计日工 prices
 *     off the BOQ or tier ③. It is not editable here — a table of trades belongs in its own screen.
 *
 * The 4.1.2 arithmetic (下浮 10%, 管理费利润 3%, 询价家数, 消耗量标准) is editable because those are
 * contract terms that differ per project, but the ORDER (`arithmetic_order`) is not offered: G9 pins it
 * to discount-then-fee and `instructions/pricing.ts` is the only thing allowed to change that.
 */
function InstructionSettingsPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['instruction-settings'], queryFn: getInstructionSettings });
  const s = data?.instructions;
  const [draft, setDraft] = useState<Partial<InstructionSettings>>({});
  const [holidaysText, setHolidaysText] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dirty = Object.keys(draft).length > 0 || holidaysText !== null;

  if (!s) return <div className="card" style={{ marginBottom: 14 }}><Skeleton lines={4} /></div>;

  const val = <K extends keyof InstructionSettings>(k: K): InstructionSettings[K] =>
    (draft[k] !== undefined ? draft[k] : s[k]) as InstructionSettings[K];
  const set = <K extends keyof InstructionSettings>(k: K, v: InstructionSettings[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const num = (k: keyof InstructionSettings, label: string, hint?: string) => (
    <div className="field" style={{ marginBottom: 8, minWidth: 150 }}>
      <label>{label}</label>
      <input type="number" step="0.01" min={0} style={{ minHeight: 44, width: 120 }}
        value={String(val(k) ?? '')}
        onChange={(e) => set(k, (e.target.value === '' ? 0 : Number(e.target.value)) as never)} />
      {hint && <div className="small muted">{hint}</div>}
    </div>
  );
  const bool = (k: keyof InstructionSettings, label: string) => (
    <label className="row small" style={{ gap: 6, alignItems: 'center', minHeight: 44, flex: '1 1 260px' }}>
      <input type="checkbox" checked={!!val(k)} onChange={(e) => set(k, e.target.checked as never)} />
      {label}
    </label>
  );

  async function save() {
    const patch: Partial<InstructionSettings> = { ...draft };
    if (holidaysText !== null) {
      // One date per line, blanks ignored. The server dedupes, sorts and refuses anything that is not
      // a real YYYY-MM-DD — so a typo comes back as a named 400 rather than silently shifting a deadline.
      patch.holidays = holidaysText.split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
    }
    setBusy(true);
    try {
      await saveInstructionSettings(patch);
      toast('工程指令设置已保存 / instruction settings saved', { kind: 'success' });
      setDraft({});
      setHolidaysText(null);
      qc.invalidateQueries({ queryKey: ['instruction-settings'] });
    } catch (e) { toast(apiErrText(e), { kind: 'error' }); }
    finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="row-wrap" style={{ alignItems: 'center', gap: 10 }}>
        <strong>📝 工程指令设置 / Instruction settings</strong>
        <span className="muted small">合同 4.1.2 / 9.1 / 9.2 的参数：时限、计价、容错额度、节假日</span>
        <span style={{ flex: 1 }} />
        <button className="btn-primary" style={{ minHeight: 44 }} disabled={busy || !dirty} onClick={save}>💾 保存 / Save</button>
      </div>

      {/* ── 容错额度: the one setting the company has not decided. Shipped inert, on purpose. ── */}
      <div style={{ marginTop: 10, borderTop: '1px solid var(--rule)', paddingTop: 8 }}>
        <strong className="small">容错额度 / Tolerance</strong>
        <div className="small muted" style={{ marginBottom: 6 }}>
          预估金额超过该额度的指令须先经商务/公司批准。留空 = 未设定，系统只提示不拦截（谢工 7/24：由公司拍板）。
          / an instruction estimated above this needs 商务 sign-off; blank means unset and the gate stays inert.
        </div>
        <div className="row-wrap" style={{ gap: 10, alignItems: 'flex-end' }}>
          <div className="field" style={{ marginBottom: 8 }}>
            <label>额度 ¥ / tolerance（留空 = 未设定）</label>
            <input type="number" step="1" min={0} style={{ minHeight: 44, width: 160 }}
              value={val('tolerance_rmb') == null ? '' : String(val('tolerance_rmb'))}
              onChange={(e) => set('tolerance_rmb', (e.target.value === '' ? null : Number(e.target.value)) as never)} />
          </div>
          <div className="field" style={{ marginBottom: 8 }}>
            <label>执行方式 / enforcement</label>
            <select style={{ minHeight: 44 }} value={String(val('tolerance_enforce_at'))}
              onChange={(e) => set('tolerance_enforce_at', e.target.value as never)}>
              <option value="advisory">提示（不拦截）/ advisory — warn only</option>
              <option value="gate">拦截（需先批准）/ gate — refuse until approved</option>
            </select>
          </div>
        </div>
      </div>

      {/* ── 4.1.2 计价 ── */}
      <div style={{ marginTop: 10, borderTop: '1px solid var(--rule)', paddingTop: 8 }}>
        <strong className="small">计价参数 / Pricing (合同 4.1.2)</strong>
        <div className="row-wrap" style={{ gap: 12, alignItems: 'flex-end' }}>
          <div className="field" style={{ marginBottom: 8, minWidth: 260 }}>
            <label>默认消耗量标准 / default consumption standard</label>
            <select style={{ minHeight: 44, width: '100%' }} value={String(val('variation_standard'))}
              onChange={(e) => set('variation_standard', e.target.value as never)}>
              {Object.keys(VARIATION_STANDARD_LABEL).map((k) => <option key={k} value={k}>{VARIATION_STANDARD_LABEL[k]}</option>)}
            </select>
          </div>
          {num('discount_pct', '下浮 % / discount', '④ 组价：基价先下浮')}
          {num('overhead_profit_pct', '管理费+利润 % / overhead & profit', '④ 组价：下浮后再加，仅 ④ 适用')}
          {num('min_quotations', '每方最少询价家数 / min quotations per party', '4.1.2：中位数还须发包人书面确认')}
        </div>
      </div>

      {/* ── 时限 ── */}
      <div style={{ marginTop: 10, borderTop: '1px solid var(--rule)', paddingTop: 8 }}>
        <strong className="small">时限 / Clocks</strong>
        <div className="row-wrap" style={{ gap: 12, alignItems: 'flex-end' }}>
          {num('quote_days', '劳务报价期限（天）/ quote window', '9.1.3 · 自变更产生日起算')}
          {num('daywork_due_days', '计日工应报（天）/ daywork due', '9.2.11 · 次日前上报')}
          {num('daywork_hard_bar_days', '计日工失权（天）/ hard bar', '48 小时 = 2 天，逾期不可恢复')}
          {num('wtl_pricing_days', '我方审核期限（天）/ our pricing SLA', 'G6 · 逾期在台账标红')}
          {num('wtl_daywork_verify_working_days', '我方核实（工作日）/ verify SLA', 'G6 · 计日工核实')}
          {num('rework_confirm_working_days', '返工确认（工作日）/ rework confirm', '9.1.4 · 逾期升级，不自动通过')}
          {num('claim_ack_days', '索赔答复（天）/ claim ack', '暂未接入流程 / not wired yet')}
          {num('sweep_interval_min', '巡检间隔（分钟）/ sweep interval', '改动后需重启服务 / needs a restart')}
        </div>
      </div>

      {/* ── 工作日 / 节假日 ── */}
      <div style={{ marginTop: 10, borderTop: '1px solid var(--rule)', paddingTop: 8 }}>
        <strong className="small">工作日与节假日 / Working days & holidays</strong>
        <div className="row-wrap" style={{ gap: 12 }}>
          {bool('working_days_exclude_weekends', '工作日不含周末 / weekends are not working days')}
          {bool('client_origin_requires_commercial_ack', '「甲方提出」需先经商务确认 / client-raised needs 商务 ack')}
          {bool('photo_required_on_quote', '劳务报价必须附照片 / photo required on a quote')}
          {bool('photo_required_on_daywork', '计日工日报必须附照片 / photo required on a daywork sheet')}
        </div>
        <div className="field">
          <label>节假日 / holidays（每行一个 YYYY-MM-DD，一行一天）</label>
          <textarea rows={4} style={{ width: '100%', fontFamily: 'monospace' }}
            value={holidaysText ?? (s.holidays ?? []).join('\n')}
            onChange={(e) => setHolidaysText(e.target.value)}
            placeholder={'2027-01-01\n2027-02-16'} />
          <div className="small muted">
            系统内没有中国法定节假日表；未填写时「工作日」只排除周末，春节等长假的 3 工作日/2 工作日时限会偏早。
            / there is no CN holiday calendar in the box — until this is filled in, 工作日 means "not a weekend".
          </div>
        </div>
      </div>
    </div>
  );
}
